import { useMemo, useState, useEffect, useRef } from 'react';
import {
  useProduct, useCreateProduct, useUpdateProduct, useCategories, useBrands,
  useUnits, useTaxProfiles, useVendors, useCompositeItem, useSaveCompositeItem, useProducts
} from '../hooks/useCatalogApi';
import { useChartOfAccountsList } from '@/features/accounts/hooks/useChartOfAccounts';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { useHasPermission } from '@/shared/permissions/useHasPermission';
import { useUser } from '@/shared/hooks/useAuth';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import toast from 'react-hot-toast';
import { Plus, Trash2 } from 'lucide-react';
import { CatalogDocumentSlot, toCatalogDocument, type CatalogDocument } from './CatalogDocumentSlot';

interface Props { productId?: string; onBack: (submittedPending?: boolean) => void; }

interface BomLine { productId: string; productName: string; quantity: string; unitCost: string; }


const PACKAGING_TYPE_OPTIONS = ['Box', 'Bucket', 'Can', 'Barrel', 'Spray', 'Bottle', 'Pouch', 'Other'];

const EMPTY_FORM = {
  name: '', sku: '', barcode: '', productType: 'single', description: '', hsnSac: '',
  categoryId: '', brandId: '', unitId: '', taxProfileId: '', preferredVendorId: '',
  incomeAccountId: '', expenseAccountId: '', taxAccountId: '',
  purchasePrice: '', sellingPrice: '', marginPercent: '', minSellingPrice: '',
  isStockManaged: true, alertQuantity: '', notForSelling: false,
  // RBM Lubricants Hierarchy — the same product name in different grades and
  // pack sizes (e.g. "RBM Hydraulic" → grade "68" → "210 L"). The Products
  // list, the RBM 3-tier selector used on quotations, and the search dropdown
  // on order/pipeline line items all group and display by these four fields
  // (src/api-catalog/products.js's /hierarchy route builds its grouping from
  // exactly these), so leaving them blank here means every product falls into
  // one ungrouped bucket everywhere else in the app.
  baseProductName: '', grade: '', packSize: '', packagingType: 'Box',
  // qtyPerPack: how many unit packs per case/carton (e.g. 16 for "1.2 LTR × 16")
  qtyPerPack: '',
  // withMark: whether the pack carries the RBM brand mark
  withMark: true,
};

export function ProductForm({ productId, onBack }: Props) {
  const isEdit = !!productId;
  const user = useUser();
  const isAdminApprover = ['superadmin', 'admin', 'department_admin', 'ceo', 'owner'].includes(user?.role || '') ||
    Boolean(user?.username === 'account' || /abhij/i.test(user?.username || '') || /abhij/i.test((user as any)?.display_name || (user as any)?.name || '') || (user as any)?.department === 'accounts');
  const { data: existing } = useProduct(productId || '');
  const { data: categories } = useCategories();
  const { data: brands } = useBrands();
  const { data: units } = useUnits();
  const { data: taxProfiles } = useTaxProfiles();
  const { data: vendors } = useVendors();
  const { data: chartOfAccounts } = useChartOfAccountsList();
  const { data: allProducts } = useProducts({ page: 1, search: '' });
  const createMut = useCreateProduct();
  const updateMut = useUpdateProduct();

  // BOM (composite items)
  const [bomLines, setBomLines] = useState<BomLine[]>([]);
  const [bomNotes, setBomNotes] = useState('');
  const { data: existingBom } = useCompositeItem(isEdit && existing?.productType === 'combo' ? productId! : null);
  const saveBomMut = useSaveCompositeItem();

  useEffect(() => {
    if (existingBom && existingBom.lines) {
      setBomLines(existingBom.lines.map((l: any) => ({
        productId: l.productId?._id || l.productId || '',
        productName: l.productId?.name || '',
        quantity: String(l.quantity || 1),
        unitCost: String(l.unitCost || 0),
      })));
      setBomNotes(existingBom.notes || '');
    }
  }, [existingBom]);

  // TDS/MSDS — kept out of `form`/`formEdits` the same way bomLines is: this
  // is uploaded-file metadata, not a typed field, and is only ever set by
  // POST /api/files/upload succeeding, never by the user typing into it.
  const [tdsDocument, setTdsDocument] = useState<CatalogDocument | null>(null);
  const [msdsDocument, setMsdsDocument] = useState<CatalogDocument | null>(null);
  const [uploadingTds, setUploadingTds] = useState(false);
  const [uploadingMsds, setUploadingMsds] = useState(false);

  useEffect(() => {
    if (existing) {
      setTdsDocument(toCatalogDocument((existing as any).tdsDocument));
      setMsdsDocument(toCatalogDocument((existing as any).msdsDocument));
    }
  }, [existing]);

  async function uploadCatalogDocument(docType: 'tds' | 'msds', file: File) {
    const setUploading = docType === 'tds' ? setUploadingTds : setUploadingMsds;
    const setDoc = docType === 'tds' ? setTdsDocument : setMsdsDocument;
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('category', 'catalog-document');
      const { data } = await api.post('/api/files/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDoc({ storageKey: data.storageKey, originalName: data.originalName, mimeType: data.mimeType, size: data.size, uploadedAt: data.uploadedAt });
      toast.success(`${docType.toUpperCase()} uploaded — Save Product to keep it.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, `Failed to upload ${docType.toUpperCase()}.`));
    } finally {
      setUploading(false);
    }
  }

  const [formEdits, setFormEdits] = useState<Partial<typeof EMPTY_FORM>>({});
  const baseForm = useMemo(() => {
    if (isEdit && existing) {
      return {
        name: existing.name || '', sku: existing.sku || '', barcode: existing.barcode || '',
        productType: existing.productType || 'single', description: existing.description || '', hsnSac: existing.hsnSac || '',
        categoryId: existing.categoryId?._id || '', brandId: existing.brandId?._id || '',
        unitId: existing.unitId?._id || '', taxProfileId: existing.taxProfileId?._id || '',
        preferredVendorId: existing.preferredVendorId?._id || existing.preferredVendorId || '',
        incomeAccountId: (existing as any).incomeAccountId?._id || (existing as any).incomeAccountId || '',
        expenseAccountId: (existing as any).expenseAccountId?._id || (existing as any).expenseAccountId || '',
        taxAccountId: (existing as any).taxAccountId?._id || (existing as any).taxAccountId || '',
        purchasePrice: String(existing.purchasePrice || ''), sellingPrice: String(existing.sellingPrice || ''),
        marginPercent: String(existing.marginPercent || ''),
        minSellingPrice: String((existing as any).minSellingPrice || ''),
        isStockManaged: existing.isStockManaged ?? true, alertQuantity: String(existing.alertQuantity || ''),
        notForSelling: existing.notForSelling || false,
        baseProductName: (existing as any).baseProductName || '', grade: (existing as any).grade || '',
        packSize: (existing as any).packSize || '', packagingType: (existing as any).packagingType || 'Box',
        qtyPerPack: String((existing as any).qtyPerPack || ''),
        withMark: (existing as any).withMark !== false,
      };
    }
    return EMPTY_FORM;
  }, [existing, isEdit]);

  const canSetFloor = useHasPermission('pricing.set_floor');


  const form = { ...baseForm, ...formEdits };
  const purchasePrice = Number(form.purchasePrice) || 0;
  const sellingPrice = Number(form.sellingPrice) || 0;
  const profitPerUnit = sellingPrice - purchasePrice;
  const calculatedMarkup = purchasePrice > 0 ? (profitPerUnit / purchasePrice) * 100 : 0;

  const setField = (k: string, v: any) => setFormEdits(p => ({ ...p, [k]: v }));

  // ── Auto-fill: brand selected → populate baseProductName (if blank) ─────────
  // When brand "RBM" is selected, baseProductName pre-fills with "RBM" so the
  // user doesn't have to type it again in the hierarchy section.
  useEffect(() => {
    const brandName = (brands || []).find(b => b._id === form.brandId)?.name || '';
    if (brandName && !form.baseProductName.trim()) {
      setFormEdits(p => ({ ...p, baseProductName: brandName }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.brandId, brands]);

  // ── Auto-SKU generation ──────────────────────────────────────────────────────
  // Format: BASENAME-GRADE-PACKSIZE-WM/NM
  // e.g. "RBM-20W50CF-SL-1.2LTR-WM"  or  "RBM-20W50CF-SL-1.2LTR-NM"
  // Only auto-generates when SKU is empty or was previously auto-generated.
  const skuWasAuto = useRef(false);
  useEffect(() => {
    // On edit: don't overwrite an existing manually-set SKU on first load
    if (isEdit && !skuWasAuto.current && form.sku.trim()) return;
    const timer = setTimeout(() => {
      const parts: string[] = [];
      if (form.baseProductName.trim()) parts.push(form.baseProductName.trim().replace(/\s+/g, '-').toUpperCase());
      if (form.grade.trim()) parts.push(form.grade.trim().replace(/\s+/g, '').toUpperCase());
      if (form.packSize.trim()) parts.push(form.packSize.trim().replace(/\s+/g, '').toUpperCase());
      parts.push(form.withMark ? 'WM' : 'NM');
      if (parts.length <= 1) return; // need at least 2 meaningful parts
      const generated = parts.join('-');
      skuWasAuto.current = true;
      setFormEdits(p => ({ ...p, sku: generated }));
    }, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.baseProductName, form.grade, form.packSize, form.withMark]);

  const handleSubmit = async () => {
    if (!form.name.trim()) return toast.error('Product name is required.');
    const payload = {
      ...form,
      purchasePrice: Number(form.purchasePrice) || 0,
      sellingPrice: Number(form.sellingPrice) || 0,
      minSellingPrice: Number(form.minSellingPrice) || 0,
      minSellingPriceMinor: Math.round((Number(form.minSellingPrice) || 0) * 100),
      marginPercent: Number(calculatedMarkup.toFixed(2)),
      alertQuantity: Number(form.alertQuantity) || 0,
      categoryId: form.categoryId || undefined,
      brandId: form.brandId || undefined,
      unitId: form.unitId || undefined,
      taxProfileId: form.taxProfileId || undefined,
      preferredVendorId: form.preferredVendorId || undefined,
      incomeAccountId: form.incomeAccountId || undefined,
      expenseAccountId: form.expenseAccountId || undefined,
      taxAccountId: form.taxAccountId || undefined,
      ...(tdsDocument ? { tdsDocument: { storageKey: tdsDocument.storageKey, originalname: tdsDocument.originalName, mimeType: tdsDocument.mimeType, size: tdsDocument.size, uploadedAt: tdsDocument.uploadedAt } } : {}),
      ...(msdsDocument ? { msdsDocument: { storageKey: msdsDocument.storageKey, originalname: msdsDocument.originalName, mimeType: msdsDocument.mimeType, size: msdsDocument.size, uploadedAt: msdsDocument.uploadedAt } } : {}),
      qtyPerPack: Number(form.qtyPerPack) || 0,
      withMark: form.withMark,
    };
    try {
      let savedProduct: any;
      let submittedPending = false;
      if (isEdit) {
        savedProduct = await updateMut.mutateAsync({ id: productId, ...payload });
        // Anyone may edit; a non-approver's change waits for an approver.
        if (savedProduct?.pendingApproval) {
          toast.success(savedProduct.message || 'Changes sent for Admin approval.', { duration: 6000 });
          savedProduct = savedProduct.product;
        } else {
          toast.success(savedProduct?.message === 'Nothing was changed.' ? 'Nothing was changed.' : 'Product updated');
        }
      } else {
        savedProduct = await createMut.mutateAsync(payload);
        if (isAdminApprover) {
          toast.success('Product created and active in catalog');
        } else {
          submittedPending = true;
          toast.success('Product submitted for Admin approval! Opening Pending Approval list...', { duration: 5000 });
        }
      }

      // Save BOM if combo product
      const pid = savedProduct?._id || productId;
      if (form.productType === 'combo' && bomLines.length > 0 && pid) {
        await saveBomMut.mutateAsync({
          parentProductId: pid,
          lines: bomLines.map(l => ({ productId: l.productId, quantity: Number(l.quantity) || 1, unitCost: Number(l.unitCost) || 0 })),
          notes: bomNotes,
        });
      }
      onBack(submittedPending);
    } catch (err: any) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const addBomLine = () => setBomLines(prev => [...prev, { productId: '', productName: '', quantity: '1', unitCost: '0' }]);
  const removeBomLine = (idx: number) => setBomLines(prev => prev.filter((_, i) => i !== idx));
  const updateBomLine = (idx: number, field: keyof BomLine, value: string) => {
    setBomLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };
  const selectBomProduct = (idx: number, pid: string) => {
    const prod = (allProducts?.data || []).find(p => p._id === pid);
    setBomLines(prev => prev.map((l, i) => i === idx ? { ...l, productId: pid, productName: prod?.name || '', unitCost: String(prod?.purchasePrice || 0) } : l));
  };

  const categoryOptions = [{ value: '', label: '— None —' }, ...(categories || []).map(c => ({ value: c._id, label: c.name }))];
  const brandOptions = [{ value: '', label: '— None —' }, ...(brands || []).map(b => ({ value: b._id, label: b.name }))];
  const unitOptions = [{ value: '', label: '— None —' }, ...(units || []).map(u => ({ value: u._id, label: `${u.name} (${u.shortName})` }))];
  const taxOptions = [{ value: '', label: '— None —' }, ...(taxProfiles || []).map(t => ({
    value: t._id,
    label: `${t.name} (Local ${t.cgstRate + t.sgstRate}% / Interstate ${t.igstRate}%)`,
  }))];
  const vendorOptions = [{ value: '', label: '— None —' }, ...(vendors || []).map(v => ({ value: v._id, label: v.name }))];
  const accountOptions = [{ value: '', label: '— Default System Account —' }, ...(chartOfAccounts || []).map(a => ({
    value: a._id,
    label: `${a.code} - ${a.name} (${a.type})`,
  }))];
  const bomProductOptions = (allProducts?.data || []).filter(p => p._id !== productId && p.productType !== 'combo').map(p => ({ value: p._id, label: `${p.name} (${p.sku || 'no SKU'})` }));

  return (
    <Card className="shadow-xs">
      <CardHeader title={isEdit ? 'Edit Product' : 'New Product'} action={
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => onBack(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={createMut.isPending || updateMut.isPending}>
            {(createMut.isPending || updateMut.isPending) ? 'Saving...' : (isEdit ? 'Save Product' : (isAdminApprover ? 'Save Product' : 'Submit for Admin Approval'))}
          </Button>
        </div>
      } />
      <CardBody className="space-y-6">
        {!isAdminApprover && !isEdit && (
          <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-900 dark:text-amber-200 flex items-center gap-2">
            <span className="font-bold shrink-0">Admin Approval Workflow:</span>
            <span>As a team member / BDM, your new product will be queued for review and approval by Admin, Dept Admin, or Super Admin before appearing as active in quotations and invoices. You can track your pending items under the "Pending Approval" tab.</span>
          </div>
        )}
        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2"><label className="text-sm font-medium">Product Name *</label><Input name="name" value={form.name} onChange={e => setField('name', e.target.value)} placeholder="e.g. Website Development Service" /></div>
          <div><label className="text-sm font-medium">Type</label><Select name="productType" value={form.productType} onChange={e => setField('productType', e.target.value)} options={[{ value: 'single', label: 'Single' }, { value: 'variable', label: 'Variable (has variants)' }, { value: 'combo', label: 'Combo/Bundle' }]} /></div>
          <div><label className="text-sm font-medium">SKU</label><Input name="sku" value={form.sku} onChange={e => setField('sku', e.target.value)} placeholder="Auto or manual" /></div>
          <div><label className="text-sm font-medium">Barcode</label><Input name="barcode" value={form.barcode} onChange={e => setField('barcode', e.target.value)} /></div>
          <div><label className="text-sm font-medium">HSN/SAC</label><Input name="hsnSac" value={form.hsnSac} onChange={e => setField('hsnSac', e.target.value)} /></div>
        </div>

        {/* RBM Lubricants Hierarchy — feeds the Products list badges, the RBM
            3-tier selector on quotations/orders, and the product search
            dropdown on line items. Leaving these blank is what makes every
            product look identical to those other screens. */}
        <div className="bg-slate-50/70 dark:bg-slate-900/40 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3">
          <div>
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">RBM Product Hierarchy</h3>
            <p className="text-xs text-muted-foreground">
              Groups this item on the Products list and the 3-tier selector: Product Name → Grade → Pack Size.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div><label className="text-sm font-medium">Product Name (Series)</label><Input name="baseProductName" value={form.baseProductName} onChange={e => setField('baseProductName', e.target.value)} placeholder="e.g. RBM Hydraulic" /></div>
            <div><label className="text-sm font-medium">Grade</label><Input name="grade" value={form.grade} onChange={e => setField('grade', e.target.value)} placeholder="e.g. 68" /></div>
            <div><label className="text-sm font-medium">Pack Size</label><Input name="packSize" value={form.packSize} onChange={e => setField('packSize', e.target.value)} placeholder="e.g. 1.2 LTR" /></div>
            <div>
              <label className="text-sm font-medium">Qty / Carton (×)</label>
              <Input name="qtyPerPack" type="number" min="0" step="1" value={form.qtyPerPack} onChange={e => setField('qtyPerPack', e.target.value)} placeholder="e.g. 16" />
              <p className="text-[11px] text-muted-foreground mt-1">
                Units per case — shows as "{String(form.packSize || '').trim() || '1.2 LTR'} × {String(form.qtyPerPack ?? '').trim() || '16'}"
              </p>
            </div>
            <div><label className="text-sm font-medium">Packaging Type</label><Select name="packagingType" value={form.packagingType} onChange={e => setField('packagingType', e.target.value)} options={PACKAGING_TYPE_OPTIONS.map(t => ({ value: t, label: t }))} /></div>
          </div>
          {/* Brand mark toggle */}
          <div className="flex items-center gap-3 pt-1">
            <input
              type="checkbox"
              id="withMark"
              checked={form.withMark}
              onChange={e => setField('withMark', e.target.checked)}
              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
            />
            <label htmlFor="withMark" className="text-sm font-medium cursor-pointer select-none">
              With Mark
              <span className="ml-1.5 text-xs text-muted-foreground font-normal">(un-check for unbranded / without-mark variants)</span>
            </label>
            {!form.withMark && (
              <span className="text-[11px] bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded font-semibold">Without Mark</span>
            )}
          </div>
        </div>

        {/* Classification & Preferred Vendor */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div><label className="text-sm font-medium">Category</label><Select name="categoryId" value={form.categoryId} onChange={e => setField('categoryId', e.target.value)} options={categoryOptions} /></div>
          <div><label className="text-sm font-medium">Brand</label><Select name="brandId" value={form.brandId} onChange={e => setField('brandId', e.target.value)} options={brandOptions} /></div>
          <div><label className="text-sm font-medium">Unit</label><Select name="unitId" value={form.unitId} onChange={e => setField('unitId', e.target.value)} options={unitOptions} /></div>
          <div><label className="text-sm font-medium">Tax Profile</label><Select name="taxProfileId" value={form.taxProfileId} onChange={e => setField('taxProfileId', e.target.value)} options={taxOptions} /></div>
          <div><label className="text-sm font-medium">Preferred Vendor</label><Select name="preferredVendorId" value={form.preferredVendorId} onChange={e => setField('preferredVendorId', e.target.value)} options={vendorOptions} /></div>
        </div>

        {/* Ledger Account Mapping */}
        <div className="bg-blue-50/70 dark:bg-blue-950/40 p-4 rounded-xl border border-blue-200 dark:border-blue-800 space-y-3">
          <div>
            <h3 className="text-sm font-bold text-blue-900 dark:text-blue-200 flex items-center gap-2">
              <span>Accounting & General Ledger Mapping</span>
            </h3>
            <p className="text-xs text-blue-700 dark:text-blue-300">
              Map this product to explicit Chart of Accounts accounts. When a Sales Invoice is raised, it will auto-post to the selected Income and Tax accounts in the General Ledger.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Income Account (Sales)</label>
              <Select name="incomeAccountId" value={form.incomeAccountId} onChange={e => setField('incomeAccountId', e.target.value)} options={accountOptions} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Expense Account (Purchase)</label>
              <Select name="expenseAccountId" value={form.expenseAccountId} onChange={e => setField('expenseAccountId', e.target.value)} options={accountOptions} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Tax Account (GST)</label>
              <Select name="taxAccountId" value={form.taxAccountId} onChange={e => setField('taxAccountId', e.target.value)} options={accountOptions} />
              <p className="text-[11px] text-muted-foreground mt-1">Default: 2200 - Output GST Payable</p>
            </div>
          </div>
        </div>

        {/* BOM Editor for Combo/Bundle products */}
        {form.productType === 'combo' && (
          <div>
            <h3 className="text-sm font-semibold mb-1">Bundle Components (BOM)</h3>
            <p className="mb-3 text-xs text-muted-foreground">Define which products make up this bundle. Stock is tracked as a single SKU for the combo itself.</p>
            <div className="space-y-2">
              {bomLines.map((line, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 border rounded-lg bg-muted/20">
                  <div className="flex-1">
                    <Select
                      name={`bom-product-${idx}`}
                      value={line.productId}
                      onChange={e => selectBomProduct(idx, e.target.value)}
                      options={[{ value: '', label: 'Select a component product...' }, ...bomProductOptions]}
                    />
                  </div>
                  <div className="w-24">
                    <Input name={`bom-qty-${idx}`} type="number" min="0.01" step="0.01" value={line.quantity} onChange={e => updateBomLine(idx, 'quantity', e.target.value)} placeholder="Qty" />
                  </div>
                  <div className="w-28">
                    <Input name={`bom-cost-${idx}`} type="number" min="0" value={line.unitCost} onChange={e => updateBomLine(idx, 'unitCost', e.target.value)} placeholder="Cost" />
                  </div>
                  <button onClick={() => removeBomLine(idx)} className="p-1.5 text-destructive hover:bg-destructive/10 rounded" aria-label="Remove line"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="mt-2" onClick={addBomLine}><Plus className="h-4 w-4 mr-1" />Add Component</Button>
            {bomLines.length > 0 && (
              <div className="mt-2 p-2 rounded-lg border bg-muted/30">
                <span className="text-xs text-muted-foreground">BOM Total Cost: </span>
                <span className="text-sm font-semibold">₹{bomLines.reduce((s, l) => s + (Number(l.quantity) * Number(l.unitCost)), 0).toLocaleString('en-IN')}</span>
              </div>
            )}
            <div className="mt-2"><Input name="bom-notes" value={bomNotes} onChange={e => setBomNotes(e.target.value)} placeholder="BOM notes (optional)" /></div>
          </div>
        )}

        {/* Pricing */}
        <div>
          <h3 className="text-sm font-semibold mb-1">Pricing</h3>
          <p className="mb-3 text-xs text-muted-foreground">Rates are exclusive of tax. The selected tax profile is applied separately on invoices and POS.</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div><label className="text-sm font-medium">Buy Price / Cost (₹)</label><Input name="purchasePrice" type="number" min="0" value={form.purchasePrice} onChange={e => setField('purchasePrice', e.target.value)} /><p className="mt-1 text-xs text-muted-foreground">Supplier se ek unit ki cost</p></div>
            <div><label className="text-sm font-medium">Sell Price (₹)</label><Input name="sellingPrice" type="number" min="0" value={form.sellingPrice} onChange={e => setField('sellingPrice', e.target.value)} /><p className="mt-1 text-xs text-muted-foreground">Customer invoice/POS default rate</p></div>
            <div><label className="text-sm font-medium">Min. Selling Price (₹)</label><Input name="minSellingPrice" type="number" min="0" value={form.minSellingPrice} onChange={e => setField('minSellingPrice', e.target.value)} disabled={!canSetFloor} readOnly={!canSetFloor} /><p className="mt-1 text-xs text-muted-foreground">{canSetFloor ? 'Floor price — nothing may be quoted or invoiced below this without an override' : 'Floor price — only a Super Admin can change this'}</p></div>
            <div className="rounded-lg border bg-muted/30 p-3"><p className="text-xs text-muted-foreground">Profit per unit</p><p className={`mt-1 text-lg font-bold ${profitPerUnit >= 0 ? 'text-green-700' : 'text-red-600'}`}>₹{profitPerUnit.toLocaleString('en-IN')}</p><p className="text-xs text-muted-foreground">Sell − Buy</p></div>
          </div>
        </div>

        {/* TDS / MSDS — technical & safety data sheets. Uploaded here so the
            website's "Send me the TDS/MSDS" request can be answered
            automatically (matched to this product by SKU); staff can also
            download either from here directly. */}
        <div className="bg-amber-50/70 dark:bg-amber-950/30 p-4 rounded-xl border border-amber-200 dark:border-amber-900 space-y-3">
          <div>
            <h3 className="text-sm font-bold text-amber-900 dark:text-amber-200">TDS / MSDS</h3>
            <p className="text-xs text-amber-800/80 dark:text-amber-300/80">
              A website visitor's TDS/MSDS request is emailed automatically the moment it arrives, using whichever of these is uploaded (matched by SKU — set the SKU above first).
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CatalogDocumentSlot
              label="Technical Data Sheet (TDS)"
              doc={tdsDocument}
              uploading={uploadingTds}
              onUpload={(f) => uploadCatalogDocument('tds', f)}
              onClear={() => setTdsDocument(null)}
            />
            <CatalogDocumentSlot
              label="Material Safety Data Sheet (MSDS)"
              doc={msdsDocument}
              uploading={uploadingMsds}
              onUpload={(f) => uploadCatalogDocument('msds', f)}
              onClear={() => setMsdsDocument(null)}
            />
          </div>
        </div>

        {/* Stock */}
        <div>
          <h3 className="text-sm font-semibold mb-3">Stock Management</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.isStockManaged} onChange={e => setField('isStockManaged', e.target.checked)} className="rounded" />
              <label className="text-sm">Track inventory for this product</label>
            </div>
            {form.isStockManaged && <div><label className="text-sm font-medium">Alert Quantity</label><Input name="alertQuantity" type="number" value={form.alertQuantity} onChange={e => setField('alertQuantity', e.target.value)} placeholder="Low stock threshold" /></div>}
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.notForSelling} onChange={e => setField('notForSelling', e.target.checked)} className="rounded" />
              <label className="text-sm">Not for selling (internal use only)</label>
            </div>
          </div>
        </div>

        {/* Description */}
        <div><label className="text-sm font-medium">Description</label><textarea value={form.description} onChange={e => setField('description', e.target.value)} rows={3} className="w-full rounded-md border border-gray-300 bg-white text-gray-900 px-3 py-2 text-sm" placeholder="Product description..." /></div>
      </CardBody>
    </Card>
  );
}
